
import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useLocation } from 'react-router-dom';
import Layout from '@/components/layout/Layout';
import { Separator } from '@/components/ui/separator';
import ContactForm from '@/components/contact/ContactForm';
import { Card, CardContent } from '@/components/ui/card';
import { Mail, Calendar } from 'lucide-react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

const Contact = () => {
  const location = useLocation();
  const [activeTab, setActiveTab] = useState<string>("contact");
  
  useEffect(() => {
    // Check if we have state with defaultTab
    if (location.state && location.state.defaultTab) {
      setActiveTab(location.state.defaultTab);
    }
  }, [location]);
  
  return (
    <Layout>
      <div className="container mx-auto px-6 py-12">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <div className="max-w-3xl mx-auto text-center mb-12">
            <h1 className="text-4xl md:text-5xl font-bold mb-4">Contact Us</h1>
            <p className="text-lg text-gray-600">
              Have questions about Morgen Health? We'd love to hear from you. Fill out the form below, 
              or schedule a meeting directly with our team.
            </p>
          </div>
          
          <div className="max-w-3xl mx-auto bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
            <div className="p-6">
              <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
                <TabsList className="grid w-full grid-cols-2 mb-6">
                  <TabsTrigger value="contact" className="flex items-center gap-2">
                    <Mail className="h-4 w-4" />
                    Contact Form
                  </TabsTrigger>
                  <TabsTrigger value="calendar" className="flex items-center gap-2">
                    <Calendar className="h-4 w-4" />
                    Schedule Meeting
                  </TabsTrigger>
                </TabsList>
                
                <TabsContent value="contact">
                  <div className="mb-4">
                    <h2 className="text-2xl font-bold">Get in Touch</h2>
                    <p className="text-gray-600">Complete the form to start a conversation</p>
                  </div>
                  <Separator className="my-4" />
                  
                  {/* Contact Form */}
                  <Card className="border-0 shadow-none">
                    <CardContent className="p-0">
                      <ContactForm />
                    </CardContent>
                  </Card>
                </TabsContent>
                
                <TabsContent value="calendar">
                  <div className="mb-4">
                    <h2 className="text-2xl font-bold">Schedule a Meeting</h2>
                    <p className="text-gray-600">Book time directly with our team</p>
                  </div>
                  <Separator className="my-4" />
                  
                  {/* Calendly Integration */}
                  <div className="calendly-container" style={{ minHeight: "630px" }}>
                    <iframe
                      src="https://calendly.com/jeff-morgenhealth"
                      width="100%"
                      height="630"
                      frameBorder="0"
                      title="Schedule a meeting with Morgen Health"
                    ></iframe>
                  </div>
                </TabsContent>
              </Tabs>
              
              {/* Contact Information */}
              <div className="mt-8 border-t pt-8">
                <h3 className="text-lg font-bold mb-2">Other ways to reach us</h3>
                <div className="flex flex-col md:flex-row justify-between items-start">
                  <div>
                    <p className="text-gray-700">Morgen Health Inc.</p>
                    <p className="text-gray-600">3343 Peachtree Rd NE, Suite 145 #2956</p>
                    <p className="text-gray-600">Atlanta, GA 30326</p>
                  </div>
                  <div className="mt-4 md:mt-0">
                    <div className="flex items-center">
                      <Mail className="text-morgenBlue mr-2" />
                      <p className="text-gray-700">Email:</p>
                    </div>
                    <a href="mailto:info@morgenhealth.com" className="text-morgenBlue hover:underline">
                      info@morgenhealth.com
                    </a>
                  </div>
                </div>
              </div>
            </div>
          </div>
          
          <div className="max-w-3xl mx-auto text-center mt-8 text-sm text-gray-500">
            <p>
              Your privacy is important to us. By submitting this form, you agree to our {' '}
              <a href="#" className="text-morgenBlue hover:underline">Privacy Policy</a>.
            </p>
          </div>
        </motion.div>
      </div>
    </Layout>
  );
};

export default Contact;
